import React, { useEffect, useState } from "react";
import { adminApi } from "../../services/adminApi";

const BUCKETS = [
  { key: "expired", label: "Expired", color: "text-red-600 bg-red-50 border-red-200" },
  { key: "30_days", label: "Expiring within 30 days", color: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "60_days", label: "Expiring within 60 days", color: "text-yellow-700 bg-yellow-50 border-yellow-200" },
  { key: "90_days", label: "Expiring within 90 days", color: "text-blue-700 bg-blue-50 border-blue-200" },
];

const SOURCE_LABELS = { profile: "Profile", document: "Uploaded Doc", insurance_card: "Insurance Card" };

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = type === "error"
    ? "bg-red-50 border-red-300 text-red-700"
    : "bg-green-50 border-green-300 text-green-700";

  return (
    <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 border rounded-xl px-5 py-3 shadow-lg text-sm font-medium ${colors}`}>
      {type === "error"
        ? <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        : <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
      }
      {message}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

function LogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(log.createdAt);
  const dateStr = date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const timeStr = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => !log.skipped && setExpanded((x) => !x)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50 transition"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              log.skipped ? "bg-gray-100 text-gray-500" :
              log.trigger === "manual" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
            }`}>
              {log.skipped ? "Skipped" : log.trigger === "manual" ? "Manual" : "Automatic"}
            </span>
            {log.skipped
              ? <span className="text-sm text-gray-400">{log.skipReason}</span>
              : <span className="text-sm text-gray-700">
                  {log.emailsSent} email{log.emailsSent !== 1 ? "s" : ""} sent
                  {log.driversNotified !== log.emailsSent
                    ? ` (${log.driversNotified} drivers with items, ${log.driversNotified - log.emailsSent} failed)`
                    : ""}
                </span>
            }
          </div>
        </div>
        <div className="text-xs text-gray-400 shrink-0 text-right">
          <div>{dateStr}</div>
          <div>{timeStr}</div>
        </div>
        {!log.skipped && log.details?.length > 0 && (
          <svg className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {expanded && log.details?.length > 0 && (
        <div className="px-4 pb-3 bg-gray-50">
          {log.details.map((d, i) => (
            <div key={i} className="text-xs text-gray-600 py-1.5 border-b border-gray-100 last:border-0">
              <div className="font-medium text-gray-800">{d.driverName} — {d.driverEmail}</div>
              <div className="mt-0.5 space-x-3">
                {d.items.map((item, j) => (
                  <span key={j} className={`inline-block ${item.daysLeft < 0 ? "text-red-600" : "text-amber-600"}`}>
                    {item.label} ({item.daysLeft < 0 ? `${Math.abs(item.daysLeft)}d overdue` : `${item.daysLeft}d`})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Compliance() {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [toast, setToast] = useState(null);

  function loadLogs() {
    adminApi.getNotificationLogs().then(({ logs: l }) => setLogs(l)).catch(console.error);
  }

  useEffect(() => {
    Promise.all([
      adminApi.getCompliance(),
      adminApi.getNotificationLogs(),
    ]).then(([compData, logData]) => {
      setData(compData);
      setLogs(logData.logs);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  async function handleNotify() {
    setNotifying(true);
    try {
      const result = await adminApi.triggerNotify();
      setToast({ message: result.message || "Emails sent.", type: "success" });
      loadLogs();
    } catch (err) {
      setToast({ message: err.message || "Failed to send notifications.", type: "error" });
    } finally {
      setNotifying(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  const byBucket = {};
  for (const b of BUCKETS) {
    byBucket[b.key] = (data?.items || []).filter((i) => i.bucket === b.key);
  }

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance</h1>
          <p className="text-sm text-gray-500 mt-0.5">Notification lead time: {data?.notificationLeadDays || 30} days</p>
        </div>
        <button onClick={handleNotify} disabled={notifying}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition disabled:opacity-60">
          {notifying ? "Sending…" : "Send Expiry Emails Now"}
        </button>
      </div>

      {BUCKETS.map(({ key, label, color }) => (
        <div key={key} className={`mb-6 rounded-xl border p-5 ${color}`}>
          <h2 className="font-semibold mb-3">{label} ({byBucket[key]?.length || 0})</h2>
          {byBucket[key]?.length === 0 ? (
            <p className="text-sm opacity-70">None</p>
          ) : (
            <div className="bg-white rounded-lg overflow-hidden border border-current border-opacity-20">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-600 font-medium">Document / Field</th>
                    <th className="px-4 py-2 text-left text-gray-600 font-medium">Driver</th>
                    <th className="px-4 py-2 text-left text-gray-600 font-medium">Source</th>
                    <th className="px-4 py-2 text-left text-gray-600 font-medium">Expiry</th>
                    <th className="px-4 py-2 text-left text-gray-600 font-medium">Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byBucket[key].map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{item.label}</td>
                      <td className="px-4 py-2 text-gray-700">{item.ownerName}</td>
                      <td className="px-4 py-2 text-gray-500">{SOURCE_LABELS[item.source] || item.ownerType}</td>
                      <td className="px-4 py-2 text-gray-600">{new Date(item.expiryDate).toLocaleDateString()}</td>
                      <td className={`px-4 py-2 font-semibold ${item.daysLeft < 0 ? "text-red-600" : "text-gray-700"}`}>
                        {item.daysLeft < 0 ? `${Math.abs(item.daysLeft)}d overdue` : `${item.daysLeft}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {!data?.items?.length && (
        <div className="text-center text-gray-400 py-10">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="font-medium">All compliance items are up to date.</p>
        </div>
      )}

      {/* Notification Log */}
      <div className="mt-8">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Notification History</h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {!logs?.length ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No notifications sent yet.</p>
          ) : (
            logs.map((log) => <LogRow key={log._id} log={log} />)
          )}
        </div>
      </div>
    </div>
  );
}
